import QRCodeComponent from 'react-qr-code';
import { useTranslation } from 'react-i18next';
import { parse } from 'date-fns-jalali/parse';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { parseError } from '@/utils';
import { useThemeToken } from '@/hooks';
import { ForgetCardHttp } from '@/services';
import { isExeedForgottenCode } from '@/services/http';
import { AppModal, Flex, LoadingSpin } from '@/components';
import { SwiperItemAttrs } from '@/components/SwiperConfigable';
import { format } from 'date-fns-jalali/format';

export interface ForgetCardModalProps {
  hide: () => void;
  open: boolean;
  variant: ComponentVariant;
  dailySale: boolean;
  item: SwiperItemAttrs;
}

export function ForgetCardModal({
  item,
  hide,
  open,
  variant,
  dailySale
}: ForgetCardModalProps) {

  const [t] = useTranslation(['reserve']);
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [error, setError] = useState('');

  const { colorPrimary } = useThemeToken();

  const getQRCode = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await ForgetCardHttp.getQRCode(item.id, item.count, dailySale);
      setQrCode(response.data?.payload?.forgotCardCode ?? 'ERROR_NO_CODE');
    }
    catch (e) {
      setError(isExeedForgottenCode(e) ? t('reserve:errors.limit-forgotten-card-usage') : parseError(e));
    }
    finally {
      setLoading(false);
    }
  }, [item, dailySale, t]);

  useEffect(() => {
    getQRCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deliveryDate = useMemo(() => {
    if (!item?.data?.date) return null;
    return parse(item?.data?.date, 'yyyy/MM/dd hh:mm', new Date());
  }, []);

  return (
    <AppModal
      open={open}
      destroyOnClose
      footer={null}
      variant={variant}
      onCancel={hide}
      title={t('common:labels.forget')}
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          height: !loading || error ? 'auto' : '300px',
        }
      }}
    >
      <div className="text-center w-full h-full">
        {loading ? <LoadingSpin fillHeight className="!text-primary" size={28} /> : (
          <Flex direction="column" className="h-full">
            <div
              style={{ color: colorPrimary }}
              className="text-sm flex items-center font-bold">
              <div>{item?.title}</div>
              <div className="mx-2">{item?.data?.foodTypeTitle}</div>
              {deliveryDate &&
                <div className="ms-auto inline-flex">
                  <span>{format(deliveryDate, 'EEEE yyyy/MM/dd')}</span>
                </div>
              }
            </div>
            <div className="mt-1">
              <div className="font-bold text-lg text-dark flex mb-1">
                {t(`reserve:context.item-name-prefix.${variant}`)}
                {item?.description}
              </div>
              <div className="text-[#979797] font-bold flex mt-1">{item?.secondaryDescription}</div>
              <div className="mt-1 flex justify-start">
                {t('common:labels.count')}: {item?.count} {t('common:labels.no-')}
              </div>
              {/* <div className="flex justify-start rtl gap-1 mt-1">
                <span>{t('common:labels.date')}:</span>
                <span className="ltr inline-flex">{item?.data?.date}</span>
              </div> */}
            </div>
            {error ? (
              <b className="w-full my-auto text-center bg-red-100 text-red-500 p-3 rounded-lg">
                {error}
              </b>) : (
              <div className="flex flex-col items-center justify-center mt-4">
                <QRCodeComponent size={130} value={qrCode ?? 'ERROR'} />
                <div
                  style={{ borderTop: '1px solid #ddd' }}
                  className="text-lg mt-4 font-bold text-dark">{qrCode}</div>
              </div>
            )}
          </Flex>
        )}
      </div>
    </AppModal>
  );
};

export default ForgetCardModal;
